import { ComponentRule } from './ComponentRule';
import ComponentRuleExecutor from './ComponentRuleExecutor';
import ComponentField from './ComponentField';

class ComponentForm<T extends Record<string, any>> {
  private value: T | null;
  private readonly fields: Record<string, ComponentField>;
  private readonly errors: Record<string, ComponentRule[]>;

  private _onChange: ((errors: Record<string, ComponentRule[]>) => void) | undefined;

  constructor(value: T | null, fields: Record<string, ComponentField>) {
    this.value = value;
    this.fields = fields;
    this.errors = {};
  }

  public onChange(onChange: (errors: Record<string, ComponentRule[]>) => void): void {
    this._onChange = onChange;
  }

  public async onChangeFieldValue<T>(fieldName: string, value: T): Promise<void> {
    (this.value as Record<string, any>)[fieldName] = value;
    const field = this.getField(fieldName);
    await field.onChange(value);
    await this.validate(fieldName);
    this._onChange?.(this.errors);
  }

  public async validate(field?: string): Promise<boolean> {
    if (field) {
      await this._validateField(field);
      return this.isValid();
    }
    for (const fieldName in this.fields) {
      await this._validateField(fieldName);
    }
    return this.isValid();
  }

  private async _validateField(fieldName: string): Promise<void> {
    const errors = await ComponentRuleExecutor.execute(this.fields[fieldName], this.value?.[fieldName]);
    if (errors.length == 0) {
      delete this.errors[fieldName];
    } else {
      this.errors[fieldName] = errors;
    }
  }

  public isValid(): boolean {
    return Object.keys(this.errors).length == 0;
  }

  public reset() {
    Object.keys(this.fields).forEach((fieldName) => {
      delete this.fields[fieldName];
    });
    this.value = null;
  }

  public getField(name: string) {
    const field = this.fields[name];
    if (!field) {
      throw new Error(`Field with name ${name} not found`);
    }
    return field;
  }
}

export default ComponentForm;
